<template>
    <div>
        <h1 class="h3 mb-3 text-gray-800">
            {{ $t(titulo || "Detalhes do Acolhimento") }}
        </h1>
        <load v-if="loading" :mode="mode" />
        <div v-else id="boxPrint" class="row">

            <div class="col-md-12">
                <print-header />
                <table class="table table-bordered bg-white table-sm">
                    <tbody>
                        <tr>
                            <td>
                                ID:<b>{{ item.id }}</b> Protocolo:<b>{{
                                    item.protocolo
                                }}</b>
                            </td>
                            <td>
                                Criado:<b>{{ item.cadastrado }}</b>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                Técnico Responsável:<b v-if="item.responsavel">{{ item.responsavel.name }}</b>
                            </td>
                            <td>
                                Atualizado:<b>{{ item.atualizado }}</b>
                            </td>
                        </tr>
                        <tr>
                            <td colspan="2">
                                Unidade:<b v-if="item.unidade">{{
                                    item.unidade.nome
                                }}</b>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <template v-if="isIdoso">

                    <h5 class="font-weight-bold text-center">Identificação da Pessoa</h5>

                    <table class="table b-table b-table-stacked-md table-bordered table-sm bg-white">
                        <tbody>
                            <tr>
                                <td>
                                    Nome:
                                    <b>{{ item.nome_acolhido }} </b>
                                </td>
                                <td>
                                    Idade:
                                    <b>{{ calcularIdade(item.nascimento) }}</b>
                                </td>
                                <td>
                                    CPF:
                                    <strong>{{ item.cpf | VMask("###.###.##-##") }}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    Data de Nascimento:
                                    <strong v-if="item.nascimento">
                                        {{formatDate(item.nascimento)}}
                                    </strong>
                                </td>
                                <td colspan="2">
                                    Naturalidade:
                                    <strong>
                                        {{ item.naturalidade }}
                                    </strong>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    Situação Conjugal:
                                    <strong>{{ getValue( item.estado_civil, $root.arrays.estado_civil) }}</strong>
                                </td>
                                <td colspan="2">
                                    Telefone:
                                    <strong> {{ item.telefone_contato }}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td colspan="3">
                                    Endereço:
                                    <strong> {{ item.local_origem }} </strong>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <h5 class="font-weight-bold text-center"> Dados do Acolhimento </h5>

                    <table class="table b-table b-table-stacked-md table-bordered table-sm bg-white">
                        <tbody>
                            <tr>
                                <td>
                                    Origem da Solicitação:
                                    <b>{{ getValue(item.origem_solicitacao_id, $root.arrays.acolhimento_origem_solicitacao) }} </b>
                                </td>
                                <td>
                                    Serviço Socioassistencial: 
                                    <b>{{ servicoSelecionado(item.servico_socioassistencial_id)?.value || '-' }}</b>
                                </td>
                            </tr>
                            <tr>
                                <td colspan="2">
                                    Termo do Acolhimento:
                                    <b>{{ item.termo_acolhimento }} </b>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    Data do Acolhimento:
                                    <strong v-if="item.data_acolhimento">
                                        {{formatDate(item.data_acolhimento)}}
                                    </strong>
                                </td>
                                <td>
                                    Hora do Acolhimento:
                                    <strong>
                                        {{ item.hora_acolhimento }}
                                    </strong>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    LGBTQIANP+:
                                    <b> {{ booleanText(item.lgbt_id) }} </b>
                                </td>
                                <td>
                                    Deficiência:
                                    <b> {{ booleanText(item.deficiencia) }}</b>
                                </td>
                            </tr>
                            <tr v-if="mostrarDeficiencia">
                                <td colspan="2">
                                    Tipo de Deficiência: 
                                    <b> {{ item.tipo_deficiencia }} </b>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    Situação de Rua:
                                    <b> {{ booleanText(item.situacao_rua) }} </b>
                                </td>
                                <td>
                                    Grupo de Irmãos:
                                    <b> {{ booleanText(item.grupo_irmaos) }}</b>
                                </td>
                            </tr>
                            <tr v-if="Array.isArray(item.motivo_acolhimento)">
                                <td colspan="2">
                                    Motivo(s) do Acolhimento:
                                    <b> {{ item.motivo_acolhimento.map(id => getArrayValue('motivos_acolhimento', id)).join(', ') }} </b>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div v-if="item.primeiraObservacao && item.primeiraObservacao.idoso">
                        
                        <h5 class="font-weight-bold text-center"> Primeira Observação </h5>
                        <table class="table table-sm table-bordered bg-white">
                            <tbody>
                            <tr>
                                <td>A pessoa encontrava-se em boas condições de higiene?</td>
                                <td>{{ getValue(item.primeiraObservacao.idoso.condicao_higiene, $root.arrays.pergunta) }}</td>
                            </tr>
                            <tr>
                                <td>A pessoa estava agitada ao ingressar na unidade?</td>
                                <td>{{ getValue(item.primeiraObservacao.idoso.chegou_agitada, $root.arrays.pergunta) }}</td>
                            </tr>
                            <tr>
                                <td>A pessoa apresentou dificuldade para dormir?</td>
                                <td>{{ getValue(item.primeiraObservacao.idoso.dificuldade_dormir, $root.arrays.pergunta) }}</td>
                            </tr>
                            <tr>
                                <td>A pessoa apresentava vestígios do uso de alguma substância psicoativa?</td>
                                <td>{{ getValue(item.primeiraObservacao.idoso.vestigio_psicoativo, $root.arrays.pergunta) }}</td>
                            </tr>
                            <tr>
                                <td>A pessoa apresentava algum sinal de violência física?</td>
                                <td>{{ getValue(item.primeiraObservacao.idoso.violencia_fisica, $root.arrays.pergunta) }}</td>
                            </tr>
                            <tr>
                                <td>A pessoa apresentava algum sinal de violência sexual?</td>
                                <td>{{ getValue(item.primeiraObservacao.idoso.violacia_sexual, $root.arrays.pergunta) }}</td>
                            </tr>
                            <tr>
                                <td>A pessoa apresentava aspectos que indicam a vivência de negligência?</td>
                                <td>{{ getValue(item.primeiraObservacao.idoso.vivencia_negligencia, $root.arrays.pergunta) }}</td>
                            </tr>
                            </tbody>
                        </table>

                        <h5 class="mt-3">Aspectos pessoais observados</h5>
                        <p>
                            {{ item.primeiraObservacao.idoso.aspectos_pessoais
                            ? item.primeiraObservacao.idoso.aspectos_pessoais
                                .map(id => getArrayValue('acolhimento_aspectos_pessoais_idoso', id))
                                .join(', ')
                            : '-' }}
                        </p>

                        <p><b>Outros aspectos pessoais:</b> {{ item.primeiraObservacao.idoso.aspectos_pessoais_outros || '-' }}</p>

                        <h5 class="mt-3">Percepção da Equipe SUAS</h5>
                        <p v-html="item.primeiraObservacao.idoso.parecer_equipe_tecnica || '-'"></p>
                    </div>

                    <div v-if="item.caractSaude && item.caractSaude.idoso">
                        <h5 class="font-weight-bold text-center"> Saúde e Desenvolvimento </h5>
                        <h5 class="mt-3">Linguagem</h5>
                        <table class="table table-sm table-bordered bg-white">
                            <tbody>
                            <tr>
                                <td>Apresenta dificuldade na fala?</td>
                                <td>{{ getValue(item.caractSaude.idoso.dificuldade_fala, $root.arrays.pergunta) }}</td>
                            </tr>
                            <tr>
                                <td>Compreende as perguntas que são feitas?</td>
                                <td>{{ getValue(item.caractSaude.idoso.compreende_perguntas, $root.arrays.pergunta) }}</td>
                            </tr>
                            <tr>
                                <td>Apresenta dificuldade em se expressar?</td>
                                <td>{{ getValue(item.caractSaude.idoso.dificuldade_expressar, $root.arrays.pergunta) }}</td>
                            </tr>
                            </tbody>
                        </table>

                        <h5 class="mt-3">Alimentação</h5>
                        <table class="table table-sm table-bordered bg-white">
                            <tbody>
                            <tr><td>Tem bom apetite?</td><td>{{ getValue(item.caractSaude.idoso.apetite, $root.arrays.pergunta) }}</td></tr>
                            <tr><td>Recusa alimentação?</td><td>{{ getValue(item.caractSaude.idoso.recusa_alimentar, $root.arrays.pergunta) }}</td></tr>
                            <tr>
                                <td>Apresenta restrição a algum alimento?</td>
                                <td>
                                {{ getValue(item.caractSaude.idoso.restricao_alimentar, $root.arrays.pergunta) }}
                                <div v-if="item.caractSaude.idoso.restricao_alimentar == 1" class="mt-1">
                                    <b>Se sim qual?</b> {{ item.caractSaude.idoso.restricao_alimentar_qual || '-' }}
                                </div>
                                </td>
                            </tr>
                            <tr>
                                <td>Apresenta alergia a algum alimento?</td>
                                <td>
                                {{ getValue(item.caractSaude.idoso.alergia_alimentar, $root.arrays.pergunta) }}
                                <div v-if="item.caractSaude.idoso.alergia_alimentar == 1" class="mt-1">
                                    <b>Se sim qual?</b> {{ item.caractSaude.idoso.alergia_alimentar_qual || '-' }}
                                </div>
                                </td>
                            </tr>
                            <tr><td>Necessita de ajuda para se alimentar?</td><td>{{ getValue(item.caractSaude.idoso.ajudar_alimentar, $root.arrays.pergunta) }}</td></tr>
                            <tr><td>Faz uso de sonda para alimentação?</td><td>{{ getValue(item.caractSaude.idoso.sonda_alimentar, $root.arrays.pergunta) }}</td></tr>
                            <tr><td>Apresenta preferência alimentar?</td>
                                <td>
                                {{ getValue(item.caractSaude.idoso.preferencia_alimentar, $root.arrays.pergunta) }}
                                <div v-if="item.caractSaude.idoso.preferencia_alimentar == 1" class="mt-1">
                                    <b>Se sim qual?</b> {{ item.caractSaude.idoso.preferencia_alimentar_qual || '-' }}
                                </div>
                                </td>
                            </tr>
                            </tbody>
                        </table>

                        <h5 class="mt-3">Saúde</h5>
                        <table class="table table-sm table-bordered bg-white">
                            <tbody>
                                <tr><td>Possui cartão de vacina?</td><td>{{ getValue(item.caractSaude.idoso.cartao_vacina, $root.arrays.pergunta) }}</td></tr>
                                <tr><td>O cartão está atualizado?</td><td>{{ getValue(item.caractSaude.idoso.cartao_atualizado, $root.arrays.pergunta) }}</td></tr>
                                <tr><td>Apresenta dificuldade de locomoção e/ou mobilidade reduzida?</td><td>{{ getValue(item.caractSaude.idoso.dificuldade_mobildiade, $root.arrays.pergunta) }}</td></tr>
                                <tr><td>Utiliza algum tipo de equipamento para apoio e locomoção?</td><td>{{ getValue(item.caractSaude.idoso.equipamento_locomocao, $root.arrays.pergunta) }}</td></tr>
                                <tr><td>Qual tipo de equipamento para apoio e locomoção?</td><td>{{ getValue(item.caractSaude.idoso.equipamento_locomocao_qual, $root.arrays.equipamento_locomocao) }}</td></tr>
                                <tr><td>Sofreu algum acidente ou queda?</td><td>{{ getValue(item.caractSaude.idoso.sofreu_acidente, $root.arrays.pergunta) }}</td></tr>
                                <tr v-if="item.caractSaude.idoso.sofreu_acidente == 1"><td>Como reagiu?</td><td>{{ item.caractSaude.idoso.acidente_reagiu || '-' }}</td></tr>
                                <tr><td>Faz uso de órtese?</td><td>{{ getValue(item.caractSaude.idoso.uso_ortese, $root.arrays.pergunta) }}</td></tr>
                                <tr v-if="item.caractSaude.idoso.uso_ortese == 1"><td>Qual?</td><td>{{ item.caractSaude.idoso.ortese_qual || '-' }}</td></tr>
                                <tr><td>Faz uso de prótese?</td><td>{{ getValue(item.caractSaude.idoso.usa_protese, $root.arrays.pergunta) }}</td></tr>
                                <tr v-if="item.caractSaude.idoso.usa_protese == 1"><td>Qual?</td><td>{{ item.caractSaude.idoso.protese_qual || '-' }}</td></tr>
                                <tr><td>Faz uso de óculos?</td><td>{{ getValue(item.caractSaude.idoso.usa_oculos, $root.arrays.pergunta) }}</td></tr>
                                <tr><td>Apresenta doença ortopédica (pé chato, torto etc.)?</td><td>{{ getValue(item.caractSaude.idoso.doenca_ortopedica, $root.arrays.pergunta) }}</td></tr>
                                <tr v-if="item.caractSaude.idoso.doenca_ortopedica == 1"><td>Qual?</td><td>{{ item.caractSaude.idoso.doenca_ortopedica_qual || '-' }}</td></tr>
                                <tr><td>Data da última avaliação/acompanhamento em qual serviço?</td><td>{{ formatDate(item.caractSaude.idoso.data_ultima_avaliacao) }}</td></tr>
                                <tr><td>Faz uso de fralda geriátrica?</td><td>{{ getValue(item.caractSaude.idoso.usa_fralda, $root.arrays.pergunta) }}</td></tr>
                                <tr><td>Apresenta doença óssea</td><td>{{ getValue(item.caractSaude.idoso.doenca_ossea, $root.arrays.pergunta) }}</td></tr>
                                <tr v-if="item.caractSaude.idoso.doenca_ossea == 1"><td>Qual?</td><td>{{ item.caractSaude.idoso.doenca_ossea_qual || '-' }}</td></tr>
                                <tr><td>Apresenta doença psiquiátrica ou psicológica?</td><td>{{ getValue(item.caractSaude.idoso.doenca_psiquiatrica, $root.arrays.pergunta) }}</td></tr>
                                <tr v-if="item.caractSaude.idoso.doenca_psiquiatrica == 1"><td>Qual?</td><td>{{ item.caractSaude.idoso.doenca_psiquiatrica_qual || '-' }}</td></tr>
                                <tr v-if="item.caractSaude.idoso.doenca_psiquiatrica == 1"><td>Data Início do tratamento</td><td>{{ formatDate(item.caractSaude.idoso.data_inicio_tratamento) }}</td></tr>
                                <tr v-if="item.caractSaude.idoso.doenca_psiquiatrica == 1"><td>Instituição</td><td>{{ getValue(item.caractSaude.idoso.doenca_psiq_instituicao, $root.arrays.intituicao_psiquiatrica) }}</td></tr>
                                <tr v-if="item.caractSaude.idoso.doenca_psiquiatrica == 1"><td>Referência técnica se houve?</td><td>{{ item.caractSaude.idoso.referencia_tecnica || '-' }}</td></tr>
                                <tr><td>Há indicação para acompanhamento psicológico?</td><td>{{ getValue(item.caractSaude.idoso.acomp_psicologico, $root.arrays.pergunta) }}</td></tr>
                                <tr><td>Esteve ou está em acompanhamento psicológico?</td><td>{{ getValue(item.caractSaude.idoso.acomp_psicologico_esteve, $root.arrays.pergunta) }}</td></tr>
                                <tr v-if="item.caractSaude.idoso.acomp_psicologico_esteve == 1"><td>Há quanto tempo:</td><td>{{ item.caractSaude.idoso.acomp_psicologico_esteve_tempo || '-' }}</td></tr>
                                <tr><td>Apresenta doenças renais</td><td>{{ getValue(item.caractSaude.idoso.doenca_renal, $root.arrays.pergunta) }}</td></tr>
                                <tr v-if="item.caractSaude.idoso.doenca_renal == 1"><td>Qual?</td><td>{{ item.caractSaude.idoso.doenca_renal_qual || '-' }}</td></tr>
                                <tr><td>Apresenta algum tipo de comorbidade (ex: diabetes, pressão alta, doenças cardiovasculares)?</td><td>{{ getValue(item.caractSaude.idoso.tem_comorbidade, $root.arrays.pergunta) }}</td></tr>
                                <tr v-if="item.caractSaude.idoso.tem_comorbidade == 1"><td>Qual?</td><td>{{ item.caractSaude.idoso.tem_comorbidade_qual || '-' }}</td></tr>
                                <tr><td>Apresenta alguma outra doença?</td><td>{{ getValue(item.caractSaude.idoso.outra_doenca, $root.arrays.pergunta) }}</td></tr>
                                <tr v-if="item.caractSaude.idoso.outra_doenca == 1"><td>Qual?</td><td>{{ item.caractSaude.idoso.outra_doenca_qual || '-' }}</td></tr>
                                <tr><td>Apresenta algum tipo de alergia?</td><td>{{ getValue(item.caractSaude.idoso.apresenta_alergia, $root.arrays.pergunta) }}</td></tr>
                                <tr v-if="item.caractSaude.idoso.apresenta_alergia == 1"><td>Quais?</td><td>{{ item.caractSaude.idoso.alergia_qual || '-' }}</td></tr>
                                <tr><td>Possui histórico de acidente cerebral vascular (AVC)?</td><td>{{ getValue(item.caractSaude.idoso.historico_avc, $root.arrays.pergunta) }}</td></tr>
                                <tr v-if="item.caractSaude.idoso.historico_avc == 1"><td>Período?</td><td>{{ item.caractSaude.idoso.historico_avc_periodo || '-' }}</td></tr>
                                <tr><td>Possui histórico de infarto?</td><td>{{ getValue(item.caractSaude.idoso.historico_infarto, $root.arrays.pergunta) }}</td></tr>
                                <tr v-if="item.caractSaude.idoso.historico_infarto == 1"><td>Período?</td><td>{{ item.caractSaude.idoso.historico_infarto_periodo || '-' }}</td></tr>
                                <tr><td>Faz uso constante ou  prolongado de medicamentos?</td><td>{{ getValue(item.caractSaude.idoso.uso_prolongado_med, $root.arrays.pergunta) }}</td></tr>
                                <tr v-if="item.caractSaude.idoso.uso_prolongado_med == 1"><td>Quais?</td><td>{{ item.caractSaude.idoso.uso_prolongado_med_quais || '-' }}</td></tr>
                                <tr><td>Precisa de algum cuidado especial por motivo de saúde?</td><td>{{ getValue(item.caractSaude.idoso.cuidado_especial, $root.arrays.pergunta) }}</td></tr>
                                <tr v-if="item.caractSaude.idoso.cuidado_especial == 1"><td>Período?</td><td>{{ item.caractSaude.idoso.cuidado_especial_periodo || '-' }}</td></tr>
                                <tr><td>É necessário encaminhamento para Atendimento médico especializado?</td><td>{{ getValue(item.caractSaude.idoso.atendimento_medico_especializado, $root.arrays.pergunta) }}</td></tr>
                                <tr v-if="item.caractSaude.idoso.atendimento_medico_especializado == 1"><td>Quais?</td><td>{{ item.caractSaude.idoso.at_medico_especializado_quais || '-' }}</td></tr>
                            </tbody>
                        </table>
                    </div>

                    <div v-if="item.educacao && item.educacao.idoso">
                        <h5 class="font-weight-bold text-center mt-4"> Educação </h5>
                        <table class="table table-sm table-bordered bg-white">
                            <tbody>
                                <tr>
                                    <td>Frequenta escola ou creche?</td>
                                    <td>{{ getValue(item.educacao.idoso.frequenta_escola, $root.arrays.frequenta_escola) }}</td>
                                </tr>
                                <tr>
                                    <td>Sabe ler e escrever?</td>
                                    <td>{{ getValue(item.educacao.idoso.sabe_ler, $root.arrays.perguntas) }}</td>
                                </tr>
                                <tr>
                                    <td>Curso mais elevado que frequentou (com pelo menos uma série concluída)</td>
                                    <td>{{ getValue(item.educacao.idoso.curso_frequentou, $root.arrays.curso_frequentou) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div v-if="item.remuneracaoAuxilio && item.remuneracaoAuxilio.idoso">
                        <h5 class="font-weight-bold text-center mt-4">Trabalho e Renda</h5>
                        <table class="table table-sm table-bordered bg-white">
                            <tbody>
                                <tr>
                                    <td>Possui qualificação profissional?</td>
                                    <td>{{ getValue(item.remuneracaoAuxilio.idoso.possui_qualificacao, $root.arrays.pergunta) }}</td>
                                </tr>
                                <tr>
                                    <td>Profissão (conforme CBO)</td>
                                    <td>{{ getValue(item.remuneracaoAuxilio.idoso.desc_profissao, $root.ocupacoes) }}</td>
                                </tr>
                                <tr>
                                    <td>Recebe aposentadoria por algum desses motivos?</td>
                                    <td>{{ getValue(item.remuneracaoAuxilio.idoso.recebe_aposentadoria, $root.arrays.aposentadoria_motivo) }}</td>
                                </tr>
                                <tr>
                                    <td>Valor da aposentadoria</td>
                                    <td>R$ {{ item.remuneracaoAuxilio.idoso.valor_aposentadoria || '-' }}</td>
                                </tr>
                                <tr>
                                    <td>Recebe algum desses benefícios?</td>
                                    <td>{{ getValue(item.remuneracaoAuxilio.idoso.recebe_beneficio, $root.arrays.beneficios) }}</td>
                                </tr>
                                <tr>
                                    <td>Valor do benefício</td>
                                    <td>R$ {{ item.remuneracaoAuxilio.idoso.valor_beneficio || '-' }}</td>
                                </tr>
                                <tr>
                                    <td>A pessoa administra seus recursos financeiros?</td>
                                    <td>{{ getValue(item.remuneracaoAuxilio.idoso.recursos_financeiros, $root.arrays.idoso_pessoa_administra_auxilio) }}</td>
                                </tr>
                                <tr>
                                    <td>É beneficiário de Programa Social?</td>
                                    <td>{{ getValue(item.remuneracaoAuxilio.idoso.programa_social, $root.arrays.pergunta) }}</td>
                                </tr>
                                <tr v-if="item.remuneracaoAuxilio.idoso.desc_programa_social && item.remuneracaoAuxilio.idoso.desc_programa_social.length">
                                    <td>Programas ou Benefícios Sociais</td>
                                    <td>
                                        {{ item.remuneracaoAuxilio.idoso.desc_programa_social
                                        .map(id => getValue(id, $root.arrays.programa_social_acolhimento))
                                        .join(', ')
                                        }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <!-- Dados dos programas sociais -->
                        <div v-if="item.remuneracaoAuxilio.idoso.desc_programa_social && item.remuneracaoAuxilio.idoso.desc_programa_social.length">
                            <h6 class="mt-3">Detalhes dos Programas</h6>
                            <div v-for="(programaId, index) in item.remuneracaoAuxilio.idoso.desc_programa_social" :key="programaId">
                                <p>
                                <b>Programa:</b> {{ getValue(programaId, $root.arrays.programa_social_acolhimento) }} <br />
                                <b>Data de Início:</b> {{ formatDate(item.remuneracaoAuxilio.idoso['data_inicio_' + (index + 1)]) }}<br />
                                <b>Valor Mensal:</b> R$ {{ item.remuneracaoAuxilio.idoso['valor_mensal_' + (index + 1)] || '-' }}
                                </p>
                            </div>
                        </div>

                        <!-- Dados da pessoa que auxilia nos recursos -->
                        <div v-if="item.remuneracaoAuxilio.idoso.recursos_financeiros == 2">
                            <h6 class="mt-3">Pessoa que auxilia na administração dos recursos</h6>
                            <p><b>Nome:</b> {{ item.remuneracaoAuxilio.idoso.pessoa_aux_nome || '-' }}</p>
                            <p><b>Endereço:</b> {{ item.remuneracaoAuxilio.idoso.pessoa_aux_endereco || '-' }}</p>
                            <p><b>Telefone:</b> {{ item.remuneracaoAuxilio.idoso.pessoa_aux_telefone || '-' }}</p>
                            <p><b>E-mail:</b> {{ item.remuneracaoAuxilio.idoso.pessoa_aux_email || '-' }}</p>
                            <p><b>Possui procuração ou curatela?</b> {{ getValue(item.remuneracaoAuxilio.idoso.pessoa_aux_procuracao, $root.arrays.pergunta) }}</p>
                        </div>
                    </div>

                    <div v-if="item.aspectoGeral && item.aspectoGeral.idoso">
                        <h5 class="font-weight-bold text-center"> Aspectos Gerais </h5>
                        <table class="table table-sm table-bordered bg-white">
                            <tbody>
                            <tr>
                                <td>Religião</td>
                                <td>{{ getValue(item.aspectoGeral.idoso.religiao_id, $root.arrays.religiao_acolhimento) }}</td>
                            </tr>
                            <tr v-if="item.aspectoGeral.idoso.regiao">
                                <td>Descreva a religião</td>
                                <td>{{ item.aspectoGeral.idoso.regiao }}</td>
                            </tr>
                            <tr>
                                <td>Hábitos</td>
                                <td>{{ item.aspectoGeral.idoso.habitos }}</td>
                            </tr>
                            <tr>
                                <td>Hobbies/Preferências de lazer</td>
                                <td>{{ item.aspectoGeral.idoso.hobbies }}</td>
                            </tr>
                            <tr>
                                <td>Habilidades/Talento</td>
                                <td>{{ item.aspectoGeral.idoso.habilidades }}</td>
                            </tr>
                            <tr>
                                <td>Medos relevantes/traumas/dificuldades</td>
                                <td>{{ getValue(item.aspectoGeral.idoso.medos, $root.arrays.pergunta) }}</td>
                            </tr>
                            <tr v-if="item.aspectoGeral.idoso.medos == 1">
                                <td>Qual?</td>
                                <td>{{ item.aspectoGeral.idoso.medos_quais }}</td>
                            </tr>
                            <tr>
                                <td>Planos ou desejos futuros</td>
                                <td>{{ getValue(item.aspectoGeral.idoso.planos, $root.arrays.pergunta) }}</td>
                            </tr>
                            <tr v-if="item.aspectoGeral.idoso.planos == 1">
                                <td>Qual?</td>
                                <td>{{ item.aspectoGeral.idoso.planos_quais }}</td>
                            </tr>
                            <tr>
                                <td>Interesse em participar de atividades</td>
                                <td>
                                    <span v-if="Array.isArray(item.aspectoGeral.idoso.idoso_interesse_atividades)">
                                        <ul class="mb-0 pl-3">
                                            <li v-for="id in item.aspectoGeral.idoso.idoso_interesse_atividades" :key="id">
                                                {{ getValue(id, $root.arrays.idoso_interesse_atividades) }}
                                            </li>
                                        </ul>
                                    </span>
                                    <span v-else>
                                        {{ getValue(item.aspectoGeral.idoso.idoso_interesse_atividades, $root.arrays.idoso_interesse_atividades) }}
                                    </span>
                                </td>
                            </tr>
                            <tr v-if="item.aspectoGeral.idoso.idoso_interesse_outros">
                                <td>Outras</td>
                                <td>{{ item.aspectoGeral.idoso.idoso_interesse_outros }}</td>
                            </tr>
                            <tr>
                                <td>Há restrições de saúde, decisão judicial ou outro aspecto que impossibilite a saída desacompanhada ou a prática de atividade(s)?</td>
                                <td>{{ getValue(item.aspectoGeral.idoso.restricao_saude, $root.arrays.pergunta) }}</td>
                            </tr>
                            <tr>
                                <td>Rotina da pessoa antes do acolhimento</td>
                                <td>{{ item.aspectoGeral.idoso.rotina }}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <div v-if="item.metaIdoso && item.metaIdoso.idoso">

                        <h4 class="font-weight-bold text-center">Objetivos e Metas </h4>

                        <!-- Documental -->
                        <div>
                            <h5 class="font-weight-bold text-center">Situação Documental</h5>
                            <table class="table table-sm table-bordered bg-white">
                            <tbody>
                                <tr><td>Prazo</td><td>{{ formatDate(item.metaIdoso.idoso.documental_prazo) }}</td></tr>
                                <tr><td>Situação Atual</td><td v-html="item.metaIdoso.idoso.documental_situacao_atual"></td></tr>
                                <tr><td>Objetivo/Meta</td><td v-html="item.metaIdoso.idoso.documental_meta"></td></tr>
                            </tbody>
                            </table>
                        </div>

                        <!-- Processual -->
                        <div>
                            <h5 class="font-weight-bold text-center">Situação Processual</h5>
                            <table class="table table-sm table-bordered bg-white">
                            <tbody>
                                <tr><td>Prazo</td><td>{{ formatDate(item.metaIdoso.idoso.processual_prazo) }}</td></tr>
                                <tr><td>Situação Atual</td><td v-html="item.metaIdoso.idoso.processual_situacao_atual"></td></tr>
                                <tr><td>Objetivo/Meta</td><td v-html="item.metaIdoso.idoso.processual_meta"></td></tr>
                            </tbody>
                            </table>
                        </div>

                        <!-- Saúde -->
                        <div>
                            <h5 class="font-weight-bold text-center">Saúde</h5>
                            <table class="table table-sm table-bordered bg-white">
                            <tbody>
                                <tr><td>Prazo</td><td>{{ formatDate(item.metaIdoso.idoso.saude_prazo) }}</td></tr>
                                <tr><td>Situação Atual</td><td v-html="item.metaIdoso.idoso.saude_situacao_atual"></td></tr>
                                <tr><td>Objetivo/Meta</td><td v-html="item.metaIdoso.idoso.saude_meta"></td></tr>
                            </tbody>
                            </table>
                        </div>

                        <!-- Educação -->
                        <div>
                            <h5 class="font-weight-bold text-center">Educação</h5>
                            <table class="table table-sm table-bordered bg-white">
                            <tbody>
                                <tr><td>Prazo</td><td>{{ formatDate(item.metaIdoso.idoso.educacao_prazo) }}</td></tr>
                                <tr><td>Situação Atual</td><td v-html="item.metaIdoso.idoso.educacao_situacao_atual"></td></tr>
                                <tr><td>Objetivo/Meta</td><td v-html="item.metaIdoso.idoso.educacao_meta"></td></tr>
                            </tbody>
                            </table>
                        </div>

                        <!-- Profissionalização -->
                        <div>
                            <h5 class="font-weight-bold text-center">Profissionalização</h5>
                            <table class="table table-sm table-bordered bg-white">
                            <tbody>
                                <tr><td>Prazo</td><td>{{ formatDate(item.metaIdoso.idoso.profissionalizacao_prazo) }}</td></tr>
                                <tr><td>Situação Atual</td><td v-html="item.metaIdoso.idoso.profissionalizacao_situacao_atual"></td></tr>
                                <tr><td>Objetivo/Meta</td><td v-html="item.metaIdoso.idoso.profissionalizacao_meta"></td></tr>
                            </tbody>
                            </table>
                        </div>

                        <!-- Arte, Cultura, Esporte E Lazer -->
                        <div>
                            <h5 class="font-weight-bold text-center">Arte, Cultura, Esporte E Lazer</h5>
                            <table class="table table-sm table-bordered bg-white">
                            <tbody>
                                <tr><td>Prazo</td><td>{{ formatDate(item.metaIdoso.idoso.arte_prazo) }}</td></tr>
                                <tr><td>Situação Atual</td><td v-html="item.metaIdoso.idoso.arte_situacao_atual"></td></tr>
                                <tr><td>Objetivo/Meta</td><td v-html="item.metaIdoso.idoso.arte_meta"></td></tr>
                            </tbody>
                            </table>
                        </div>

                        <!-- Aspectos Sociofamiliares -->
                        <div>
                            <h5 class="font-weight-bold text-center">Aspectos Sociofamiliares</h5>
                            <table class="table table-sm table-bordered bg-white mb-3">
                            <tbody>
                                <tr><td>Prazo</td><td>{{ formatDate(item.metaIdoso.idoso.aspecto_prazo) }}</td></tr>
                                <tr>
                                <td>Possui rede de apoio (pessoas/grupos que prestam algum tipo de suporte)?</td>
                                <td>{{ getValue(item.metaIdoso.idoso.possui_rede_apoio, $root.arrays.pergunta) }}</td>
                                </tr>
                                <tr>
                                <td>Intervenção e Percepção do Profissional referente aos aspectos sociofamiliares</td>
                                <td>{{ item.metaIdoso.idoso.intervencoes_sociofamiliares }}</td>
                                </tr>
                                <tr>
                                <td>Percepção da equipe SUAS</td>
                                <td v-html="item.metaIdoso.idoso.text_equipe_suas"></td>
                                </tr>
                                <tr>
                                <td>Situação Atual</td>
                                <td v-html="item.metaIdoso.idoso.aspecto_situacao_atual"></td>
                                </tr>
                                <tr>
                                <td>Objetivo/Meta</td>
                                <td v-html="item.metaIdoso.idoso.aspecto_meta"></td>
                                </tr>
                            </tbody>
                            </table>
                        </div>

                        <!-- Condições Socioeconômicas -->
                        <div>
                            <h5 class="font-weight-bold text-center">Condições Socioeconômicas</h5>
                            <table class="table table-sm table-bordered bg-white">
                            <tbody>
                                <tr><td>Prazo</td><td>{{ formatDate(item.metaIdoso.idoso.condicao_prazo) }}</td></tr>
                                <tr><td>Situação Atual</td><td v-html="item.metaIdoso.idoso.condicao_situacao_atual"></td></tr>
                                <tr><td>Objetivo/Meta</td><td v-html="item.metaIdoso.idoso.condicao_meta"></td></tr>
                            </tbody>
                            </table>
                        </div>

                        <!-- Vulnerabilidade e Riscos -->
                        <div>
                            <h5 class="font-weight-bold text-center">Vulnerabilidade e Riscos</h5>
                            <table class="table table-sm table-bordered bg-white">
                            <tbody>
                                <tr><td>Prazo</td><td>{{ formatDate(item.metaIdoso.idoso.vulnerabildiade_prazo) }}</td></tr>
                                <tr><td>Situação Atual</td><td v-html="item.metaIdoso.idoso.vulnerabildiade_situacao_atual"></td></tr>
                                <tr><td>Objetivo/Meta</td><td v-html="item.metaIdoso.idoso.vulnerabildiade_meta"></td></tr>
                            </tbody>
                            </table>
                        </div>

                        <!-- Autonomia / autoestima / identidade -->
                        <div>
                            <h5 class="font-weight-bold text-center">Autonomia / autoestima / identidade</h5>
                            <table class="table table-sm table-bordered bg-white">
                            <tbody>
                                <tr><td>Prazo</td><td>{{ formatDate(item.metaIdoso.idoso.autonomia_prazo) }}</td></tr>
                                <tr><td>Situação Atual</td><td v-html="item.metaIdoso.idoso.autonomia_situacao_atual"></td></tr>
                                <tr><td>Objetivo/Meta</td><td v-html="item.metaIdoso.idoso.autonomia_meta"></td></tr>
                            </tbody>
                            </table>
                        </div>

                        <!-- Preparação para desligamento -->
                        <div>
                            <h5 class="font-weight-bold text-center">Preparação para desligamento</h5>
                            <table class="table table-sm table-bordered bg-white">
                                <tbody>
                                    <tr><td>Prazo</td><td>{{ formatDate(item.metaIdoso.idoso.desligamento_prazo) }}</td></tr>
                                    <tr><td>Situação Atual</td><td v-html="item.metaIdoso.idoso.desligamento_situacao_atual"></td></tr>
                                    <tr><td>Objetivo/Meta</td><td v-html="item.metaIdoso.idoso.desligamento_meta"></td></tr>
                                    <tr><td>Data do Desligamento</td><td>{{ formatDate(item.metaIdoso.idoso.data_desligamento) }}</td></tr>
                                    <tr>
                                        <td>Motivo do Desligamento</td>
                                        <td>{{ getValue(item.metaIdoso.idoso.motivo_desligamento, $root.arrays.motivo_desligamento_acolhimento) }}</td>
                                    </tr>
                                    <tr>
                                        <td>Dados da rede socioassistencial e intersetorial para acompanhamento: local(s), serviço(s), programa(s)</td>
                                        <td v-html="item.metaIdoso.idoso.dados_da_rede_socioassistencial"></td>
                                    </tr>
                                    <tr>
                                        <td>Ações pactuadas no período pós-acolhimento e prazo(s)</td>
                                        <td v-html="item.metaIdoso.idoso.acao_repactuada"></td>
                                    </tr>
                                    <tr>
                                        <td>Rede de apoio para contato</td>
                                        <td v-html="item.metaIdoso.idoso.rede_apoio_contato"></td>
                                    </tr>
                                    <tr>
                                        <td>Observações</td>
                                        <td v-html="item.metaIdoso.idoso.observacoes_desligamento"></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </template>

                <template v-else>

                    <div class="row">
                        <div class="col-12">
                            <template v-if="todasPessoas.length > 1">
                                <h3>Pessoas Vinculadas ao Acolhimento:</h3>

                                <b-table :items="todasPessoas" :fields="['nome', 'idade', 'parentesco']" small responsive>
                                    <template #cell(nome)="row">
                                        <a href="#" @click.prevent="selecionarPessoa(row.item)">
                                        {{ row.item.nome }}
                                        </a>
                                    </template>
                                </b-table>
                            </template>

                            <div
                                v-if="pessoaSelecionada"
                                :class="{ 'mt-4': todasPessoas.length > 1 }"
                            >

                                <div v-if="pessoaSelecionada && pessoaSelecionada.tipo_cadastro === 'Principal'">

                                    <!-- Isso aqui só será exibido se for o acolhido principal -->

                                    <h5 class="font-weight-bold text-center">Identificação da Pessoa</h5>

                                    <table class="table b-table b-table-stacked-md table-bordered table-sm bg-white">
                                        <tbody>
                                            <tr>
                                                <td>
                                                    Nome:
                                                    <b>{{ item.nome_acolhido }} </b>
                                                </td>
                                                <td>
                                                    Idade:
                                                    <b>{{ calcularIdade(item.nascimento) }}</b>
                                                </td>
                                                <td>
                                                    CPF:
                                                    <strong>{{ item.cpf | VMask("###.###.##-##") }}</strong>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    Data de Nascimento:
                                                    <strong v-if="item.nascimento">
                                                        {{formatDate(item.nascimento)}}
                                                    </strong>
                                                </td>
                                                <td colspan="2">
                                                    Naturalidade:
                                                    <strong>
                                                        {{ item.naturalidade }}
                                                    </strong>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    Situação Conjugal:
                                                    <strong>{{ getValue( item.estado_civil, $root.arrays.estado_civil) }}</strong>
                                                </td>
                                                <td colspan="2">
                                                    Telefone:
                                                    <strong> {{ item.telefone_contato }}</strong>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td colspan="3">
                                                    Endereço:
                                                    <strong> {{ item.local_origem }} </strong>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>

                                    <h5 class="font-weight-bold text-center"> Dados do Acolhimento </h5>

                                    <table class="table b-table b-table-stacked-md table-bordered table-sm bg-white">
                                        <tbody>
                                            <tr>
                                                <td>
                                                    Origem da Solicitação:
                                                    <b>{{ getValue(item.origem_solicitacao_id, $root.arrays.acolhimento_origem_solicitacao) }} </b>
                                                </td>
                                                <td>
                                                    Serviço Socioassistencial: 
                                                    <b>{{ servicoSelecionado(item.servico_socioassistencial_id)?.value || '-' }}</b>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td colspan="2">
                                                    Termo do Acolhimento:
                                                    <b>{{ item.termo_acolhimento }} </b>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    Data do Acolhimento:
                                                    <strong v-if="item.data_acolhimento">
                                                        {{formatDate(item.data_acolhimento)}}
                                                    </strong>
                                                </td>
                                                <td>
                                                    Hora do Acolhimento:
                                                    <strong>
                                                        {{ item.hora_acolhimento }}
                                                    </strong>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    LGBTQIANP+:
                                                    <b> {{ booleanText(item.lgbt_id) }} </b>
                                                </td>
                                                <td>
                                                    Deficiência:
                                                    <b> {{ booleanText(item.deficiencia) }}</b>
                                                </td>
                                            </tr>
                                            <tr v-if="mostrarDeficiencia">
                                                <td colspan="2">
                                                    Tipo de Deficiência: 
                                                    <b> {{ item.tipo_deficiencia }} </b>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    Situação de Rua:
                                                    <b> {{ booleanText(item.situacao_rua) }} </b>
                                                </td>
                                                <td>
                                                    Grupo de Irmãos:
                                                    <b> {{ booleanText(item.grupo_irmaos) }}</b>
                                                </td>
                                            </tr>
                                            <tr v-if="Array.isArray(item.motivo_acolhimento)">
                                                <td colspan="2">
                                                    Motivo(s) do Acolhimento:
                                                    <b> {{ item.motivo_acolhimento.map(id => getArrayValue('motivos_acolhimento', id)).join(', ') }} </b>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div v-else>

                                    <h5 class="font-weight-bold text-center">Identificação da Pessoa</h5>

                                    <table class="table b-table b-table-stacked-md table-bordered table-sm bg-white">
                                        <tbody>
                                            <tr>
                                                <td>
                                                    Nome:
                                                    <b>{{ pessoaSelecionada.nome }} </b>
                                                </td>
                                                <td>
                                                    Idade:
                                                    <b>{{ calcularIdade(pessoaSelecionada.data_nascimento) }}</b>
                                                </td>
                                                <td>
                                                    CPF:
                                                    <strong>{{ pessoaSelecionada.cpf | VMask("###.###.##-##") }}</strong>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    Data de Nascimento:
                                                    <strong v-if="pessoaSelecionada.data_nascimento">
                                                        {{formatDate(pessoaSelecionada.data_nascimento)}}
                                                    </strong>
                                                </td>
                                                <td colspan="2">
                                                    Parentesco:
                                                    <strong>
                                                        {{ pessoaSelecionada.parentesco }}
                                                    </strong>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td colspan="2">
                                                    E-mail:
                                                    <strong> {{ pessoaSelecionada.email }}</strong>
                                                </td>
                                                <td>
                                                    Telefone:
                                                    <strong> {{ pessoaSelecionada.telefone }}</strong>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td colspan="3">
                                                    Endereço:
                                                    <strong> {{ pessoaSelecionada.endereco }} </strong>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div v-if="dadosCompletosPessoaSelecionada().primeiraObservacao">
                                    
                                    <h5 class="font-weight-bold text-center"> Primeira Observação </h5>
                                    <table class="table table-sm table-bordered bg-white">
                                        <tbody>
                                            <tr>
                                                <td>A pessoa estava agitada ao ingressar na unidade?</td>
                                                <td>{{ getValue(dadosCompletosPessoaSelecionada().primeiraObservacao.chegou_agitada, $root.arrays.pergunta) }}</td>
                                            </tr>
                                            <tr>
                                                <td>A pessoa apresentou dificuldade para dormir?</td>
                                                <td>{{ getValue(dadosCompletosPessoaSelecionada().primeiraObservacao.dificuldade_dormir, $root.arrays.pergunta) }}</td>
                                            </tr>
                                            <tr>
                                                <td>A pessoa apresentava vestígios do uso de alguma substância psicoativa?</td>
                                                <td>{{ getValue(dadosCompletosPessoaSelecionada().primeiraObservacao.vestigio_psicoativo, $root.arrays.pergunta) }}</td>
                                            </tr>
                                            <tr>
                                                <td>A pessoa apresentava algum sinal de violência física?</td>
                                                <td>{{ getValue(dadosCompletosPessoaSelecionada().primeiraObservacao.violencia_fisica, $root.arrays.pergunta) }}</td>
                                            </tr>
                                            <tr>
                                                <td>A pessoa apresentava algum sinal de violência sexual?</td>
                                                <td>{{ getValue(dadosCompletosPessoaSelecionada().primeiraObservacao.violacia_sexual, $root.arrays.pergunta) }}</td>
                                            </tr>
                                            <tr>
                                                <td>A pessoa apresentava aspectos que indicam a vivência de negligência?</td>
                                                <td>{{ getValue(dadosCompletosPessoaSelecionada().primeiraObservacao.vivencia_negligencia, $root.arrays.pergunta) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>

                                    <h5 class="mt-3">Aspectos pessoais observados</h5>
                                    <p>
                                        {{ dadosCompletosPessoaSelecionada().primeiraObservacao.aspectos_pessoais
                                        ? dadosCompletosPessoaSelecionada().primeiraObservacao.aspectos_pessoais
                                            .map(id => getArrayValue('acolhimento_aspectos_pessoais_idoso', id))
                                            .join(', ')
                                        : '-' }}
                                    </p>

                                    <p><b>Outros aspectos pessoais:</b> {{ dadosCompletosPessoaSelecionada().primeiraObservacao.aspectos_pessoais_outros || '-' }}</p>

                                    <h5 class="mt-3">Percepção da Equipe SUAS</h5>
                                    <p v-html="dadosCompletosPessoaSelecionada().primeiraObservacao.parecer_equipe_tecnica || '-'"></p>
                                </div>

                                <div v-if="dadosCompletosPessoaSelecionada().caractSaude">
                                    <h5 class="font-weight-bold text-center"> Saúde e Desenvolvimento </h5>
                                    <h5 class="mt-3">Linguagem</h5>
                                    <table class="table table-sm table-bordered bg-white">
                                        <tbody>
                                        <tr>
                                            <td>Apresenta dificuldade na fala?</td>
                                            <td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.dificuldade_fala, $root.arrays.pergunta) }}</td>
                                        </tr>
                                        <tr>
                                            <td>Compreende as perguntas que são feitas?</td>
                                            <td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.compreende_perguntas, $root.arrays.pergunta) }}</td>
                                        </tr>
                                        <tr>
                                            <td>Apresenta dificuldade em se expressar?</td>
                                            <td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.dificuldade_expressar, $root.arrays.pergunta) }}</td>
                                        </tr>
                                        </tbody>
                                    </table>

                                    <h5 class="mt-3">Alimentação</h5>
                                    <table class="table table-sm table-bordered bg-white">
                                        <tbody>
                                        <tr><td>Tem bom apetite?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.apetite, $root.arrays.pergunta) }}</td></tr>
                                        <tr><td>Recusa alimentação?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.recusa_alimentar, $root.arrays.pergunta) }}</td></tr>
                                        <tr>
                                            <td>Apresenta restrição a algum alimento?</td>
                                            <td>
                                            {{ getValue(dadosCompletosPessoaSelecionada().caractSaude.restricao_alimentar, $root.arrays.pergunta) }}
                                            <div v-if="dadosCompletosPessoaSelecionada().caractSaude.restricao_alimentar == 1" class="mt-1">
                                                <b>Se sim qual?</b> {{ dadosCompletosPessoaSelecionada().caractSaude.restricao_alimentar_qual || '-' }}
                                            </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Apresenta alergia a algum alimento?</td>
                                            <td>
                                            {{ getValue(dadosCompletosPessoaSelecionada().caractSaude.alergia_alimentar, $root.arrays.pergunta) }}
                                            <div v-if="dadosCompletosPessoaSelecionada().caractSaude.alergia_alimentar == 1" class="mt-1">
                                                <b>Se sim qual?</b> {{ dadosCompletosPessoaSelecionada().caractSaude.alergia_alimentar_qual || '-' }}
                                            </div>
                                            </td>
                                        </tr>
                                        <tr><td>Necessita de ajuda para se alimentar?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.ajudar_alimentar, $root.arrays.pergunta) }}</td></tr>
                                        <tr><td>Faz uso de sonda para alimentação?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.sonda_alimentar, $root.arrays.pergunta) }}</td></tr>
                                        <tr><td>Apresenta preferência alimentar?</td>
                                            <td>
                                            {{ getValue(dadosCompletosPessoaSelecionada().caractSaude.preferencia_alimentar, $root.arrays.pergunta) }}
                                            <div v-if="dadosCompletosPessoaSelecionada().caractSaude.preferencia_alimentar == 1" class="mt-1">
                                                <b>Se sim qual?</b> {{ dadosCompletosPessoaSelecionada().caractSaude.preferencia_alimentar_qual || '-' }}
                                            </div>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>

                                    <h5 class="mt-3">Saúde</h5>
                                    <table class="table table-sm table-bordered bg-white">
                                        <tbody>
                                            <tr><td>Possui cartão de vacina?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.cartao_vacina, $root.arrays.pergunta) }}</td></tr>
                                            <tr><td>O cartão está atualizado?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.cartao_atualizado, $root.arrays.pergunta) }}</td></tr>
                                            <tr><td>Apresenta dificuldade de locomoção e/ou mobilidade reduzida?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.dificuldade_mobildiade, $root.arrays.pergunta) }}</td></tr>
                                            <tr><td>Utiliza algum tipo de equipamento para apoio e locomoção?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.equipamento_locomocao, $root.arrays.pergunta) }}</td></tr>
                                            <tr><td>Qual tipo de equipamento para apoio e locomoção?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.equipamento_locomocao_qual, $root.arrays.equipamento_locomocao) }}</td></tr>
                                            <tr><td>Sofreu algum acidente ou queda?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.sofreu_acidente, $root.arrays.pergunta) }}</td></tr>
                                            <tr v-if="dadosCompletosPessoaSelecionada().caractSaude.sofreu_acidente == 1"><td>Como reagiu?</td><td>{{ dadosCompletosPessoaSelecionada().caractSaude.acidente_reagiu || '-' }}</td></tr>
                                            <tr><td>Faz uso de órtese?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.uso_ortese, $root.arrays.pergunta) }}</td></tr>
                                            <tr v-if="dadosCompletosPessoaSelecionada().caractSaude.uso_ortese == 1"><td>Qual?</td><td>{{ dadosCompletosPessoaSelecionada().caractSaude.ortese_qual || '-' }}</td></tr>
                                            <tr><td>Faz uso de prótese?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.usa_protese, $root.arrays.pergunta) }}</td></tr>
                                            <tr v-if="dadosCompletosPessoaSelecionada().caractSaude.usa_protese == 1"><td>Qual?</td><td>{{ dadosCompletosPessoaSelecionada().caractSaude.protese_qual || '-' }}</td></tr>
                                            <tr><td>Faz uso de óculos?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.usa_oculos, $root.arrays.pergunta) }}</td></tr>
                                            <tr><td>Apresenta doença ortopédica (pé chato, torto etc.)?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.doenca_ortopedica, $root.arrays.pergunta) }}</td></tr>
                                            <tr v-if="dadosCompletosPessoaSelecionada().caractSaude.doenca_ortopedica == 1"><td>Qual?</td><td>{{ dadosCompletosPessoaSelecionada().caractSaude.doenca_ortopedica_qual || '-' }}</td></tr>
                                            <tr><td>Data da última avaliação/acompanhamento em qual serviço?</td><td>{{ formatDate(dadosCompletosPessoaSelecionada().caractSaude.data_ultima_avaliacao) }}</td></tr>
                                            <tr><td>Faz uso de fralda geriátrica?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.usa_fralda, $root.arrays.pergunta) }}</td></tr>
                                            <tr><td>Apresenta doença óssea</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.doenca_ossea, $root.arrays.pergunta) }}</td></tr>
                                            <tr v-if="dadosCompletosPessoaSelecionada().caractSaude.doenca_ossea == 1"><td>Qual?</td><td>{{ dadosCompletosPessoaSelecionada().caractSaude.doenca_ossea_qual || '-' }}</td></tr>
                                            <tr><td>Apresenta doença psiquiátrica ou psicológica?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.doenca_psiquiatrica, $root.arrays.pergunta) }}</td></tr>
                                            <tr v-if="dadosCompletosPessoaSelecionada().caractSaude.doenca_psiquiatrica == 1"><td>Qual?</td><td>{{ dadosCompletosPessoaSelecionada().caractSaude.doenca_psiquiatrica_qual || '-' }}</td></tr>
                                            <tr v-if="dadosCompletosPessoaSelecionada().caractSaude.doenca_psiquiatrica == 1"><td>Data Início do tratamento</td><td>{{ formatDate(dadosCompletosPessoaSelecionada().caractSaude.data_inicio_tratamento) }}</td></tr>
                                            <tr v-if="dadosCompletosPessoaSelecionada().caractSaude.doenca_psiquiatrica == 1"><td>Instituição</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.doenca_psiq_instituicao, $root.arrays.intituicao_psiquiatrica) }}</td></tr>
                                            <tr v-if="dadosCompletosPessoaSelecionada().caractSaude.doenca_psiquiatrica == 1"><td>Referência técnica se houve?</td><td>{{ dadosCompletosPessoaSelecionada().caractSaude.referencia_tecnica || '-' }}</td></tr>
                                            <tr><td>Há indicação para acompanhamento psicológico?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.acomp_psicologico, $root.arrays.pergunta) }}</td></tr>
                                            <tr><td>Esteve ou está em acompanhamento psicológico?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.acomp_psicologico_esteve, $root.arrays.pergunta) }}</td></tr>
                                            <tr v-if="dadosCompletosPessoaSelecionada().caractSaude.acomp_psicologico_esteve == 1"><td>Há quanto tempo:</td><td>{{ dadosCompletosPessoaSelecionada().caractSaude.acomp_psicologico_esteve_tempo || '-' }}</td></tr>
                                            <tr><td>Apresenta doenças renais</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.doenca_renal, $root.arrays.pergunta) }}</td></tr>
                                            <tr v-if="dadosCompletosPessoaSelecionada().caractSaude.doenca_renal == 1"><td>Qual?</td><td>{{ dadosCompletosPessoaSelecionada().caractSaude.doenca_renal_qual || '-' }}</td></tr>
                                            <tr><td>Apresenta algum tipo de comorbidade (ex: diabetes, pressão alta, doenças cardiovasculares)?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.tem_comorbidade, $root.arrays.pergunta) }}</td></tr>
                                            <tr v-if="dadosCompletosPessoaSelecionada().caractSaude.tem_comorbidade == 1"><td>Qual?</td><td>{{ dadosCompletosPessoaSelecionada().caractSaude.tem_comorbidade_qual || '-' }}</td></tr>
                                            <tr><td>Apresenta alguma outra doença?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.outra_doenca, $root.arrays.pergunta) }}</td></tr>
                                            <tr v-if="dadosCompletosPessoaSelecionada().caractSaude.outra_doenca == 1"><td>Qual?</td><td>{{ dadosCompletosPessoaSelecionada().caractSaude.outra_doenca_qual || '-' }}</td></tr>
                                            <tr><td>Apresenta algum tipo de alergia?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.apresenta_alergia, $root.arrays.pergunta) }}</td></tr>
                                            <tr v-if="dadosCompletosPessoaSelecionada().caractSaude.apresenta_alergia == 1"><td>Quais?</td><td>{{ dadosCompletosPessoaSelecionada().caractSaude.alergia_qual || '-' }}</td></tr>
                                            <tr><td>Possui histórico de acidente cerebral vascular (AVC)?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.historico_avc, $root.arrays.pergunta) }}</td></tr>
                                            <tr v-if="dadosCompletosPessoaSelecionada().caractSaude.historico_avc == 1"><td>Período?</td><td>{{ dadosCompletosPessoaSelecionada().caractSaude.historico_avc_periodo || '-' }}</td></tr>
                                            <tr><td>Possui histórico de infarto?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.historico_infarto, $root.arrays.pergunta) }}</td></tr>
                                            <tr v-if="dadosCompletosPessoaSelecionada().caractSaude.historico_infarto == 1"><td>Período?</td><td>{{ dadosCompletosPessoaSelecionada().caractSaude.historico_infarto_periodo || '-' }}</td></tr>
                                            <tr><td>Faz uso constante ou  prolongado de medicamentos?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.uso_prolongado_med, $root.arrays.pergunta) }}</td></tr>
                                            <tr v-if="dadosCompletosPessoaSelecionada().caractSaude.uso_prolongado_med == 1"><td>Quais?</td><td>{{ dadosCompletosPessoaSelecionada().caractSaude.uso_prolongado_med_quais || '-' }}</td></tr>
                                            <tr><td>Precisa de algum cuidado especial por motivo de saúde?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.cuidado_especial, $root.arrays.pergunta) }}</td></tr>
                                            <tr v-if="dadosCompletosPessoaSelecionada().caractSaude.cuidado_especial == 1"><td>Período?</td><td>{{ dadosCompletosPessoaSelecionada().caractSaude.cuidado_especial_periodo || '-' }}</td></tr>
                                            <tr><td>É necessário encaminhamento para Atendimento médico especializado?</td><td>{{ getValue(dadosCompletosPessoaSelecionada().caractSaude.atendimento_medico_especializado, $root.arrays.pergunta) }}</td></tr>
                                            <tr v-if="dadosCompletosPessoaSelecionada().caractSaude.atendimento_medico_especializado == 1"><td>Quais?</td><td>{{ dadosCompletosPessoaSelecionada().caractSaude.at_medico_especializado_quais || '-' }}</td></tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div v-if="dadosCompletosPessoaSelecionada().educacao">
                                    <h5 class="font-weight-bold text-center mt-4"> Educação </h5>
                                    <table class="table table-sm table-bordered bg-white">
                                        <tbody>
                                            <tr>
                                                <td>Frequenta escola ou creche?</td>
                                                <td>{{ getValue(dadosCompletosPessoaSelecionada().educacao.frequenta_escola, $root.arrays.frequenta_escola) }}</td>
                                            </tr>
                                            <tr>
                                                <td>Sabe ler e escrever?</td>
                                                <td>{{ getValue(dadosCompletosPessoaSelecionada().educacao.sabe_ler, $root.arrays.perguntas) }}</td>
                                            </tr>
                                            <tr>
                                                <td>Curso mais elevado que frequentou (com pelo menos uma série concluída)</td>
                                                <td>{{ getValue(dadosCompletosPessoaSelecionada().educacao.curso_frequentou, $root.arrays.curso_frequentou) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div v-if="dadosCompletosPessoaSelecionada().remuneracaoAuxilio">
                                    <h5 class="font-weight-bold text-center mt-4">Trabalho e Renda</h5>
                                    <table class="table table-sm table-bordered bg-white">
                                        <tbody>
                                            <tr>
                                                <td>Possui qualificação profissional?</td>
                                                <td>{{ getValue(dadosCompletosPessoaSelecionada().remuneracaoAuxilio.possui_qualificacao, $root.arrays.pergunta) }}</td>
                                            </tr>
                                            <tr>
                                                <td>Profissão (conforme CBO)</td>
                                                <td>{{ getValue(dadosCompletosPessoaSelecionada().remuneracaoAuxilio.desc_profissao, $root.ocupacoes) }}</td>
                                            </tr>
                                            <tr>
                                                <td>Recebe aposentadoria por algum desses motivos?</td>
                                                <td>{{ getValue(dadosCompletosPessoaSelecionada().remuneracaoAuxilio.recebe_aposentadoria, $root.arrays.aposentadoria_motivo) }}</td>
                                            </tr>
                                            <tr>
                                                <td>Valor da aposentadoria</td>
                                                <td>R$ {{ dadosCompletosPessoaSelecionada().remuneracaoAuxilio.valor_aposentadoria || '-' }}</td>
                                            </tr>
                                            <tr>
                                                <td>Recebe algum desses benefícios?</td>
                                                <td>{{ getValue(dadosCompletosPessoaSelecionada().remuneracaoAuxilio.recebe_beneficio, $root.arrays.beneficios) }}</td>
                                            </tr>
                                            <tr>
                                                <td>Valor do benefício</td>
                                                <td>R$ {{ dadosCompletosPessoaSelecionada().remuneracaoAuxilio.valor_beneficio || '-' }}</td>
                                            </tr>
                                            <tr>
                                                <td>A pessoa administra seus recursos financeiros?</td>
                                                <td>{{ getValue(dadosCompletosPessoaSelecionada().remuneracaoAuxilio.recursos_financeiros, $root.arrays.idoso_pessoa_administra_auxilio) }}</td>
                                            </tr>
                                            <tr>
                                                <td>É beneficiário de Programa Social?</td>
                                                <td>{{ getValue(dadosCompletosPessoaSelecionada().remuneracaoAuxilio.programa_social, $root.arrays.pergunta) }}</td>
                                            </tr>
                                            <tr v-if="dadosCompletosPessoaSelecionada().remuneracaoAuxilio.desc_programa_social && dadosCompletosPessoaSelecionada().remuneracaoAuxilio.desc_programa_social.length">
                                                <td>Programas ou Benefícios Sociais</td>
                                                <td>
                                                    {{ dadosCompletosPessoaSelecionada().remuneracaoAuxilio.desc_programa_social
                                                    .map(id => getValue(id, $root.arrays.programa_social_acolhimento))
                                                    .join(', ')
                                                    }}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>

                                    <!-- Dados dos programas sociais -->
                                    <div v-if="dadosCompletosPessoaSelecionada().remuneracaoAuxilio.desc_programa_social && dadosCompletosPessoaSelecionada().remuneracaoAuxilio.desc_programa_social.length">
                                        <h6 class="mt-3">Detalhes dos Programas</h6>
                                        <div v-for="(programaId, index) in dadosCompletosPessoaSelecionada().remuneracaoAuxilio.desc_programa_social" :key="programaId">
                                            <p>
                                            <b>Programa:</b> {{ getValue(programaId, $root.arrays.programa_social_acolhimento) }} <br />
                                            <b>Data de Início:</b> {{ formatDate(dadosCompletosPessoaSelecionada().remuneracaoAuxilio['data_inicio_' + (index + 1)]) }}<br />
                                            <b>Valor Mensal:</b> R$ {{ dadosCompletosPessoaSelecionada().remuneracaoAuxilio['valor_mensal_' + (index + 1)] || '-' }}
                                            </p>
                                        </div>
                                    </div>

                                    <!-- Dados da pessoa que auxilia nos recursos -->
                                    <div v-if="dadosCompletosPessoaSelecionada().remuneracaoAuxilio.recursos_financeiros == 2">
                                        <h6 class="mt-3">Pessoa que auxilia na administração dos recursos</h6>
                                        <p><b>Nome:</b> {{ dadosCompletosPessoaSelecionada().remuneracaoAuxilio.pessoa_aux_nome || '-' }}</p>
                                        <p><b>Endereço:</b> {{ dadosCompletosPessoaSelecionada().remuneracaoAuxilio.pessoa_aux_endereco || '-' }}</p>
                                        <p><b>Telefone:</b> {{ dadosCompletosPessoaSelecionada().remuneracaoAuxilio.pessoa_aux_telefone || '-' }}</p>
                                        <p><b>E-mail:</b> {{ dadosCompletosPessoaSelecionada().remuneracaoAuxilio.pessoa_aux_email || '-' }}</p>
                                        <p><b>Possui procuração ou curatela?</b> {{ getValue(dadosCompletosPessoaSelecionada().remuneracaoAuxilio.pessoa_aux_procuracao, $root.arrays.pergunta) }}</p>
                                    </div>
                                </div>

                                <div v-if="dadosCompletosPessoaSelecionada().aspectoGeral">
                                    <h5 class="font-weight-bold text-center"> Aspectos Gerais </h5>
                                    <table class="table table-sm table-bordered bg-white">
                                        <tbody>
                                        <tr>
                                            <td>Religião</td>
                                            <td>{{ getValue(dadosCompletosPessoaSelecionada().aspectoGeral.religiao_id, $root.arrays.religiao_acolhimento) }}</td>
                                        </tr>
                                        <tr v-if="dadosCompletosPessoaSelecionada().aspectoGeral.regiao">
                                            <td>Descreva a religião</td>
                                            <td>{{ dadosCompletosPessoaSelecionada().aspectoGeral.regiao }}</td>
                                        </tr>
                                        <tr>
                                            <td>Hábitos</td>
                                            <td>{{ dadosCompletosPessoaSelecionada().aspectoGeral.habitos }}</td>
                                        </tr>
                                        <tr>
                                            <td>Hobbies/Preferências de lazer</td>
                                            <td>{{ dadosCompletosPessoaSelecionada().aspectoGeral.hobbies }}</td>
                                        </tr>
                                        <tr>
                                            <td>Habilidades/Talento</td>
                                            <td>{{ dadosCompletosPessoaSelecionada().aspectoGeral.habilidades }}</td>
                                        </tr>
                                        <tr>
                                            <td>Medos relevantes/traumas/dificuldades</td>
                                            <td>{{ getValue(dadosCompletosPessoaSelecionada().aspectoGeral.medos, $root.arrays.pergunta) }}</td>
                                        </tr>
                                        <tr v-if="dadosCompletosPessoaSelecionada().aspectoGeral.medos == 1">
                                            <td>Qual?</td>
                                            <td>{{ dadosCompletosPessoaSelecionada().aspectoGeral.medos_quais }}</td>
                                        </tr>
                                        <tr>
                                            <td>Planos ou desejos futuros</td>
                                            <td>{{ getValue(dadosCompletosPessoaSelecionada().aspectoGeral.planos, $root.arrays.pergunta) }}</td>
                                        </tr>
                                        <tr v-if="dadosCompletosPessoaSelecionada().aspectoGeral.planos == 1">
                                            <td>Qual?</td>
                                            <td>{{ dadosCompletosPessoaSelecionada().aspectoGeral.planos_quais }}</td>
                                        </tr>
                                        <tr>
                                            <td>Interesse em participar de atividades</td>
                                            <td>
                                                <span v-if="Array.isArray(dadosCompletosPessoaSelecionada().aspectoGeral.idoso_interesse_atividades)">
                                                    <ul class="mb-0 pl-3">
                                                        <li v-for="id in dadosCompletosPessoaSelecionada().aspectoGeral.idoso_interesse_atividades" :key="id">
                                                            {{ getValue(id, $root.arrays.idoso_interesse_atividades) }}
                                                        </li>
                                                    </ul>
                                                </span>
                                                <span v-else>
                                                    {{ getValue(dadosCompletosPessoaSelecionada().aspectoGeral.idoso_interesse_atividades, $root.arrays.idoso_interesse_atividades) }}
                                                </span>
                                            </td>
                                        </tr>
                                        <tr v-if="dadosCompletosPessoaSelecionada().aspectoGeral.idoso_interesse_outros">
                                            <td>Outras</td>
                                            <td>{{ dadosCompletosPessoaSelecionada().aspectoGeral.idoso_interesse_outros }}</td>
                                        </tr>
                                        <tr>
                                            <td>Há restrições de saúde, decisão judicial ou outro aspecto que impossibilite a saída desacompanhada ou a prática de atividade(s)?</td>
                                            <td>{{ getValue(dadosCompletosPessoaSelecionada().aspectoGeral.restricao_saude, $root.arrays.pergunta) }}</td>
                                        </tr>
                                        <tr>
                                            <td>Rotina da pessoa antes do acolhimento</td>
                                            <td>{{ dadosCompletosPessoaSelecionada().aspectoGeral.rotina }}</td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div v-if="dadosCompletosPessoaSelecionada().metaIdoso">

                                    <h4 class="font-weight-bold text-center">Objetivos e Metas </h4>

                                    <!-- Documental -->
                                    <div>
                                        <h5 class="font-weight-bold text-center">Situação Documental</h5>
                                        <table class="table table-sm table-bordered bg-white">
                                        <tbody>
                                            <tr><td>Prazo</td><td>{{ formatDate(dadosCompletosPessoaSelecionada().metaIdoso.documental_prazo) }}</td></tr>
                                            <tr><td>Situação Atual</td><td v-html="dadosCompletosPessoaSelecionada().metaIdoso.documental_situacao_atual"></td></tr>
                                            <tr><td>Objetivo/Meta</td><td v-html="dadosCompletosPessoaSelecionada().metaIdoso.documental_meta"></td></tr>
                                        </tbody>
                                        </table>
                                    </div>

                                    <!-- Processual -->
                                    <div>
                                        <h5 class="font-weight-bold text-center">Situação Processual</h5>
                                        <table class="table table-sm table-bordered bg-white">
                                        <tbody>
                                            <tr><td>Prazo</td><td>{{ formatDate(dadosCompletosPessoaSelecionada().metaIdoso.processual_prazo) }}</td></tr>
                                            <tr><td>Situação Atual</td><td v-html="dadosCompletosPessoaSelecionada().metaIdoso.processual_situacao_atual"></td></tr>
                                            <tr><td>Objetivo/Meta</td><td v-html="dadosCompletosPessoaSelecionada().metaIdoso.processual_meta"></td></tr>
                                        </tbody>
                                        </table>
                                    </div>

                                    <!-- Saúde -->
                                    <div>
                                        <h5 class="font-weight-bold text-center">Saúde</h5>
                                        <table class="table table-sm table-bordered bg-white">
                                        <tbody>
                                            <tr><td>Prazo</td><td>{{ formatDate(dadosCompletosPessoaSelecionada().metaIdoso.saude_prazo) }}</td></tr>
                                            <tr><td>Situação Atual</td><td v-html="dadosCompletosPessoaSelecionada().metaIdoso.saude_situacao_atual"></td></tr>
                                            <tr><td>Objetivo/Meta</td><td v-html="dadosCompletosPessoaSelecionada().metaIdoso.saude_meta"></td></tr>
                                        </tbody>
                                        </table>
                                    </div>

                                    <!-- Educação -->
                                    <div>
                                        <h5 class="font-weight-bold text-center">Educação</h5>
                                        <table class="table table-sm table-bordered bg-white">
                                        <tbody>
                                            <tr><td>Prazo</td><td>{{ formatDate(dadosCompletosPessoaSelecionada().metaIdoso.educacao_prazo) }}</td></tr>
                                            <tr><td>Situação Atual</td><td v-html="dadosCompletosPessoaSelecionada().metaIdoso.educacao_situacao_atual"></td></tr>
                                            <tr><td>Objetivo/Meta</td><td v-html="dadosCompletosPessoaSelecionada().metaIdoso.educacao_meta"></td></tr>
                                        </tbody>
                                        </table>
                                    </div>

                                    <!-- Profissionalização -->
                                    <div>
                                        <h5 class="font-weight-bold text-center">Profissionalização</h5>
                                        <table class="table table-sm table-bordered bg-white">
                                        <tbody>
                                            <tr><td>Prazo</td><td>{{ formatDate(dadosCompletosPessoaSelecionada().metaIdoso.profissionalizacao_prazo) }}</td></tr>
                                            <tr><td>Situação Atual</td><td v-html="dadosCompletosPessoaSelecionada().metaIdoso.profissionalizacao_situacao_atual"></td></tr>
                                            <tr><td>Objetivo/Meta</td><td v-html="dadosCompletosPessoaSelecionada().metaIdoso.profissionalizacao_meta"></td></tr>
                                        </tbody>
                                        </table>
                                    </div>

                                    <!-- Arte, Cultura, Esporte E Lazer -->
                                    <div>
                                        <h5 class="font-weight-bold text-center">Arte, Cultura, Esporte E Lazer</h5>
                                        <table class="table table-sm table-bordered bg-white">
                                        <tbody>
                                            <tr><td>Prazo</td><td>{{ formatDate(dadosCompletosPessoaSelecionada().metaIdoso.arte_prazo) }}</td></tr>
                                            <tr><td>Situação Atual</td><td v-html="dadosCompletosPessoaSelecionada().metaIdoso.arte_situacao_atual"></td></tr>
                                            <tr><td>Objetivo/Meta</td><td v-html="dadosCompletosPessoaSelecionada().metaIdoso.arte_meta"></td></tr>
                                        </tbody>
                                        </table>
                                    </div>

                                    <!-- Aspectos Sociofamiliares -->
                                    <div>
                                        <h5 class="font-weight-bold text-center">Aspectos Sociofamiliares</h5>
                                        <table class="table table-sm table-bordered bg-white mb-3">
                                        <tbody>
                                            <tr><td>Prazo</td><td>{{ formatDate(dadosCompletosPessoaSelecionada().metaIdoso.aspecto_prazo) }}</td></tr>
                                            <tr>
                                            <td>Possui rede de apoio (pessoas/grupos que prestam algum tipo de suporte)?</td>
                                            <td>{{ getValue(dadosCompletosPessoaSelecionada().metaIdoso.possui_rede_apoio, $root.arrays.pergunta) }}</td>
                                            </tr>
                                            <tr>
                                            <td>Intervenção e Percepção do Profissional referente aos aspectos sociofamiliares</td>
                                            <td>{{ dadosCompletosPessoaSelecionada().metaIdoso.intervencoes_sociofamiliares }}</td>
                                            </tr>
                                            <tr>
                                            <td>Percepção da equipe SUAS</td>
                                            <td v-html="dadosCompletosPessoaSelecionada().metaIdoso.text_equipe_suas"></td>
                                            </tr>
                                            <tr>
                                            <td>Situação Atual</td>
                                            <td v-html="dadosCompletosPessoaSelecionada().metaIdoso.aspecto_situacao_atual"></td>
                                            </tr>
                                            <tr>
                                            <td>Objetivo/Meta</td>
                                            <td v-html="dadosCompletosPessoaSelecionada().metaIdoso.aspecto_meta"></td>
                                            </tr>
                                        </tbody>
                                        </table>
                                    </div>

                                    <!-- Condições Socioeconômicas -->
                                    <div>
                                        <h5 class="font-weight-bold text-center">Condições Socioeconômicas</h5>
                                        <table class="table table-sm table-bordered bg-white">
                                        <tbody>
                                            <tr><td>Prazo</td><td>{{ formatDate(dadosCompletosPessoaSelecionada().metaIdoso.condicao_prazo) }}</td></tr>
                                            <tr><td>Situação Atual</td><td v-html="dadosCompletosPessoaSelecionada().metaIdoso.condicao_situacao_atual"></td></tr>
                                            <tr><td>Objetivo/Meta</td><td v-html="dadosCompletosPessoaSelecionada().metaIdoso.condicao_meta"></td></tr>
                                        </tbody>
                                        </table>
                                    </div>

                                    <!-- Vulnerabilidade e Riscos -->
                                    <div>
                                        <h5 class="font-weight-bold text-center">Vulnerabilidade e Riscos</h5>
                                        <table class="table table-sm table-bordered bg-white">
                                        <tbody>
                                            <tr><td>Prazo</td><td>{{ formatDate(dadosCompletosPessoaSelecionada().metaIdoso.vulnerabildiade_prazo) }}</td></tr>
                                            <tr><td>Situação Atual</td><td v-html="dadosCompletosPessoaSelecionada().metaIdoso.vulnerabildiade_situacao_atual"></td></tr>
                                            <tr><td>Objetivo/Meta</td><td v-html="dadosCompletosPessoaSelecionada().metaIdoso.vulnerabildiade_meta"></td></tr>
                                        </tbody>
                                        </table>
                                    </div>

                                    <!-- Autonomia / autoestima / identidade -->
                                    <div>
                                        <h5 class="font-weight-bold text-center">Autonomia / autoestima / identidade</h5>
                                        <table class="table table-sm table-bordered bg-white">
                                        <tbody>
                                            <tr><td>Prazo</td><td>{{ formatDate(dadosCompletosPessoaSelecionada().metaIdoso.autonomia_prazo) }}</td></tr>
                                            <tr><td>Situação Atual</td><td v-html="dadosCompletosPessoaSelecionada().metaIdoso.autonomia_situacao_atual"></td></tr>
                                            <tr><td>Objetivo/Meta</td><td v-html="dadosCompletosPessoaSelecionada().metaIdoso.autonomia_meta"></td></tr>
                                        </tbody>
                                        </table>
                                    </div>

                                    <!-- Preparação para desligamento -->
                                    <div>
                                        <h5 class="font-weight-bold text-center">Preparação para desligamento</h5>
                                        <table class="table table-sm table-bordered bg-white">
                                            <tbody>
                                                <tr><td>Prazo</td><td>{{ formatDate(dadosCompletosPessoaSelecionada().metaIdoso.desligamento_prazo) }}</td></tr>
                                                <tr><td>Situação Atual</td><td v-html="dadosCompletosPessoaSelecionada().metaIdoso.desligamento_situacao_atual"></td></tr>
                                                <tr><td>Objetivo/Meta</td><td v-html="dadosCompletosPessoaSelecionada().metaIdoso.desligamento_meta"></td></tr>
                                                <tr><td>Data do Desligamento</td><td>{{ formatDate(dadosCompletosPessoaSelecionada().metaIdoso.data_desligamento) }}</td></tr>
                                                <tr>
                                                    <td>Motivo do Desligamento</td>
                                                    <td>{{ getValue(dadosCompletosPessoaSelecionada().metaIdoso.motivo_desligamento, $root.arrays.motivo_desligamento_acolhimento) }}</td>
                                                </tr>
                                                <tr>
                                                    <td>Dados da rede socioassistencial e intersetorial para acompanhamento: local(s), serviço(s), programa(s)</td>
                                                    <td v-html="dadosCompletosPessoaSelecionada().metaIdoso.dados_da_rede_socioassistencial"></td>
                                                </tr>
                                                <tr>
                                                    <td>Ações pactuadas no período pós-acolhimento e prazo(s)</td>
                                                    <td v-html="dadosCompletosPessoaSelecionada().metaIdoso.acao_repactuada"></td>
                                                </tr>
                                                <tr>
                                                    <td>Rede de apoio para contato</td>
                                                    <td v-html="dadosCompletosPessoaSelecionada().metaIdoso.rede_apoio_contato"></td>
                                                </tr>
                                                <tr>
                                                    <td>Observações</td>
                                                    <td v-html="dadosCompletosPessoaSelecionada().metaIdoso.observacoes_desligamento"></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                            </div>

                        </div>

                    </div>

                </template>

                <print-footer 
                    v-if=" item.responsavel.tecnico.assinatura_digital" 
                    :tecnico="item.responsavel.tecnico" 
                    class="mt-0" 
                    :assinatura=" item.responsavel.tecnico.assinatura_digital"
                    :wpp="wpp"
                    :email="email"
                />
              
                <div class="row pt-4 px-0 d-flex justify-content-end no-print">
                    <div class="col-auto px-2 text-right">
                        <router-link v-if="mode != 'table'" :to="`/dashboard/acolhimentos/idoso/${item.unidade_id}`"
                            class="btn btn-default">
                            <i class="mdi mdi-arrow-left" />
                        </router-link>

                        <a class="btn btn-sm py-1 btn-primary" @click="print()">
                            <i class="mdi mdi-printer mdi-18px" />
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import axios from 'axios';
import moment from 'moment';

export default {
    props: ["titulo", "mode"],
    name: "Show",
    data() {
        return {
            pessoaSelecionada: null,
            loading: true,
            item: {},
            app_cidade: this.$store.state.settings.app_cidade,
            app_data_atual: this.$store.state.settings.app_data_atual,
            tipos: [],
            tecnicos: [],
            unidades: [],
            servicos: [],
            pessoaReferencia: [],
        };
    },
    mounted() {
        this.getServicos();
        this.getItem();
    },
    computed: {
        isIdoso() {

            const hoje = moment();
            const nascimento = moment(this.item.nascimento, "YYYY-MM-DD");
            const idade = hoje.diff(nascimento, 'years');

            // Se existir público alvo, verifica se é 8, senão usa idade para decidir
            return this.item.solicitacao?.publico_alvo_id === 8 || idade >= 60;
        },
        todasPessoas() {
            if (!this.item || !this.pessoaReferencia) return [];

            const pessoaPrincipal = {
                id: 'principal',
                nome: this.item.nome_acolhido,
                idade: this.calcularIdade(this.item.nascimento),
                parentesco: 'Acolhido(a)',
                tipo_cadastro: 'Principal',
            };

            const vinculados = this.pessoaReferencia.filter(
                p => p.tipo_cadastro === 'Familiar' || p.tipo_cadastro === 'Crianca'
            );

            return [pessoaPrincipal, ...vinculados];
        },
    },
    methods: {
        selecionarPessoa(pessoa) {
            this.pessoaSelecionada = pessoa;
        },
        getDadosModuloPorPessoa(modulo, pessoa) {
            if (!this.item || !this.item[modulo]) return null;

            const dadosModulo = this.item[modulo];

            if (pessoa.tipo_cadastro === 'Principal') {
                return dadosModulo.idoso || null;
            }

            // Para vinculados
            if (Array.isArray(dadosModulo.pessoasVinculadas)) {
                return dadosModulo.pessoasVinculadas.find(d => String(d.pessoa_referencia_id) === String(pessoa.id)) || null;
            }

            return null;
        },
        dadosCompletosPessoaSelecionada() {
            if (!this.pessoaSelecionada) return null;

            const modulos = [
                'metaIdoso',
                'educacao',
                'remuneracaoAuxilio',
                'aspectoGeral',
                'caractSaude',
                'primeiraObservacao'
            ];

            const resultado = {};

            modulos.forEach(modulo => {
                resultado[modulo] = this.getDadosModuloPorPessoa(modulo, this.pessoaSelecionada);
            });

            return resultado;
        },
        getArrayValue(arrayKey, id) {
            const lista = this.$root.arrays[arrayKey] || [];
            const item = lista.find(el => el.id == id);
            return item ? item.value : '-';
        },
        mostrarDeficiencia() {
            return this.item.deficiencia == 1;
        },
        mostrarMotivoOutro() {
            return this.item.motivo_acolhimento?.some(id => id === 19);
        },
        booleanText(val) {
            return val == 1 ? 'Sim' : 'Não';
        },
        servicoSelecionado(id) {
            return this.servicos.find(s => s.id == id) || {};
        },
        getServicos() {
            return axios.get(`api/dashboard/atendimento/servicos`).then((response) => {
                this.servicos = response.data;
            });
        },
        formatDate(dateStr) {
            if (!dateStr || dateStr === '0000-00-00') return '-';
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return '-';
            return date.toLocaleDateString('pt-BR');
        },
        calcularIdade(dataNascimento) {
            if (!dataNascimento) return '-';
            const hoje = new Date();
            const nascimento = new Date(dataNascimento);
            let idade = hoje.getFullYear() - nascimento.getFullYear();
            const mes = hoje.getMonth() - nascimento.getMonth();

            if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) {
                idade--;
            }

            return idade + ' ano(s)';
        },
        getPrazo(data) {
            if (data) {
                const data_final = moment(data);
                const data_atual = moment();
                const prazo = data_final.diff(data_atual, "months");
                return prazo + " mes(es)";
            }
            return '-';
        },
        getItem() {
            this.loading = true;
            this.pessoaSelecionada = null;
            axios.get(`api/dashboard/acolhimento/idoso/${this.$route.params.id}`).then((response) => {
                this.item = response.data.data;

                this.pessoaReferencia = this.item.pessoaReferencia || [];

                try {
                    if (typeof this.item.motivo_acolhimento === 'string') {
                        this.item.motivo_acolhimento = JSON.parse(this.item.motivo_acolhimento);
                    }
                } catch (e) {
                    this.item.motivo_acolhimento = [];
                }

                if (!this.isIdoso && this.todasPessoas.length === 1) {
                    this.selecionarPessoa(this.todasPessoas[0]);
                }
            }).finally(() => {
                this.loading = false;
            }).catch((error) => {
                this.$router.push(`/dashboard/acolhimento/unidades`);
            });
        },
        getValue(value, array) {
            if (value) {
                const result = array.filter((item) => item.id == value);
                return result[0] ? result[0].value : "";
            } else {
                return "";
            }
        },
        print() {
            const modal = document.getElementById("boxPrint");
            const cloned = modal.cloneNode(true);
            let section = document.getElementById("print");

            if (!section) {
                section = document.createElement("div");
                section.id = "print";
                document.body.appendChild(section);
            }

            section.innerHTML = "";
            section.appendChild(cloned);
            window.print();
        },
    },
};
</script>

<style>
@media screen {
  #print {
    display: none;
  }
}

@media print {
  body * {
    visibility: hidden;
  }

  #print,
  #print * {
    visibility: visible;
  }

  #print {
    width: 100%;
    position: absolute;
    left: 0;
    top: 0;
  }
}
</style>

<style>
    @media screen {
        #print {
            display: none;
        }
    }

    @media print {
        body * {
            visibility: hidden;
        }

        #print,
        #print * {
            visibility: visible;
        }

        #print {
            width: 100%;
            position: absolute;
            left: 0;
            top: 0;
        }
    }
</style>
